import { ChangeEvent, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { downscaleImage } from '../../lib/imageDownscale';
import type { Goal, GoalPhoto } from '../../api/types';

interface Props {
  /** The goal whose photos are managed; null hides the dialog. */
  goal: Goal | null;
  onClose: () => void;
  onUpload: (goal: Goal, files: File[]) => Promise<void>;
  onSetCover: (goal: Goal, photo: GoalPhoto) => Promise<void>;
  onDelete: (goal: Goal, photo: GoalPhoto) => Promise<void>;
}

/**
 * Manage a goal's photos. Every action saves immediately: picked photos are
 * shrunk on the device and uploaded, the star marks the cover, the bin deletes.
 */
export function GoalPhotosDialog({ goal, onClose, onUpload, onSetCover, onDelete }: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const act = async (action: () => Promise<void>) => {
    setError('');
    setBusy(true);
    try {
      await action();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('garageCar.goals.failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleFilesSelected = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = '';
    if (!goal || files.length === 0) return;
    act(async () => onUpload(goal, await Promise.all(files.map(downscaleImage))));
  };

  const handleDelete = (photo: GoalPhoto) => {
    if (!goal || !window.confirm(t('garageCar.goals.photos.deleteConfirm'))) return;
    act(() => onDelete(goal, photo));
  };

  return (
    <Dialog open={goal !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('garageCar.goals.photos.title', { goal: goal?.title ?? '' })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCamera />}
            disabled={busy}
          >
            {busy ? t('common.uploading') : t('garageCar.goals.photos.add')}
            <input type="file" hidden multiple accept="image/*" onChange={handleFilesSelected} />
          </Button>

          {goal && goal.photos.length === 0 && (
            <Typography color="text.secondary">{t('garageCar.goals.photos.empty')}</Typography>
          )}

          {goal && goal.photos.length > 0 && (
            <ImageList cols={2} gap={8} sx={{ m: 0 }}>
              {goal.photos.map((photo) => {
                const isCover = photo.id === goal.cover_photo_id;
                return (
                  <ImageListItem key={photo.id}>
                    <img src={photo.url} alt="" loading="lazy" />
                    <ImageListItemBar
                      title={isCover ? t('garageCar.goals.photos.cover') : undefined}
                      actionIcon={
                        <Stack direction="row">
                          <Tooltip
                            title={
                              isCover
                                ? t('garageCar.goals.photos.cover')
                                : t('garageCar.goals.photos.makeCover')
                            }
                          >
                            <span>
                              <IconButton
                                sx={{ color: 'white' }}
                                disabled={busy}
                                onClick={() => !isCover && act(() => onSetCover(goal, photo))}
                                aria-label={t('garageCar.goals.photos.makeCover')}
                                aria-pressed={isCover}
                              >
                                {isCover ? <StarIcon /> : <StarBorderIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            sx={{ color: 'white' }}
                            disabled={busy}
                            onClick={() => handleDelete(photo)}
                            aria-label={t('garageCar.goals.photos.delete')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      }
                    />
                  </ImageListItem>
                );
              })}
            </ImageList>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
